import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Bot, Eye, Loader2, X } from 'lucide-react';
import { vendorContractApi, applyContractPlaceholders } from '../../services/vendorContractService';
import { useVendorProfile } from '../../services/vendorProfileService';
import { supportChatApi } from '../../services/supportChatService';
import { normalizeChatReply } from '../../utils/chatWidgetUtils';
import useVendorPlanAccess from '../../hooks/useVendorPlanAccess';
import { useToastContext } from '../../contexts/ToastContext';
import Button from '../../components/shared/Button';
import Input from '../../components/shared/Input';
import BrandImagePicker from '../../components/vendor/branding/BrandImagePicker';
import ContractRichTextEditor from '../../components/vendor/contract/ContractRichTextEditor';
import ContractBodyHtml from '../../components/vendor/contract/ContractBodyHtml';

const VendorContractCreate = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: editId } = useParams();
  const [searchParams] = useSearchParams();
  const copyFromId = searchParams.get('from');
  const isEdit = Boolean(editId);
  const { success, error: showError } = useToastContext();
  const { data: profileResp } = useVendorProfile();
  const { isFreeTier, hasActiveSubscription } = useVendorPlanAccess();
  const canUseAi = hasActiveSubscription === true && !isFreeTier;
  const hydratedFromRef = useRef(null);
  const hydratedEditRef = useRef(null);

  const vp = profileResp?.data ?? {};
  const profileLogo = vp.logo_url || vp.brand_settings?.logo_url || null;
  const vendorAddress = [
    vp.address,
    [vp.city, vp.state, vp.zip_code].filter(Boolean).join(', '),
    vp.country,
  ]
    .filter(Boolean)
    .join(' · ');

  const [templateId, setTemplateId] = useState('');
  const [title, setTitle] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [serviceDescription, setServiceDescription] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [amount, setAmount] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [notes, setNotes] = useState('');
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoInitialized, setLogoInitialized] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiError, setAiError] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);

  const { data: templatesRes, isLoading: templatesLoading } = useQuery({
    queryKey: ['contract-templates'],
    queryFn: () => vendorContractApi.templates(),
  });
  const templates = templatesRes?.data || [];

  const { data: copyFromRes, isLoading: copyLoading } = useQuery({
    queryKey: ['vendor-contract', copyFromId],
    queryFn: () => vendorContractApi.getOne(copyFromId),
    enabled: Boolean(copyFromId) && !isEdit,
  });

  const { data: editRes, isLoading: editLoading, error: editError } = useQuery({
    queryKey: ['vendor-contract', editId],
    queryFn: () => vendorContractApi.getOne(editId),
    enabled: isEdit,
  });

  // Default logo from profile on create (once profile loads)
  useEffect(() => {
    if (isEdit || copyFromId || logoInitialized) return;
    if (profileLogo) {
      setLogoUrl(profileLogo);
      setLogoInitialized(true);
    } else if (profileResp) {
      setLogoInitialized(true);
    }
  }, [isEdit, copyFromId, profileLogo, profileResp, logoInitialized]);

  useEffect(() => {
    if (!copyFromId || !copyFromRes?.data || isEdit) return;
    if (hydratedFromRef.current === copyFromId) return;
    hydratedFromRef.current = copyFromId;
    const src = copyFromRes.data;
    setTitle(src.title ? `${src.title} (copy)` : 'Contract copy');
    setBodyHtml(src.body_html || '');
    setTemplateId(src.template_id || '');
    setLogoUrl(src.logo_url || profileLogo || null);
    setLogoInitialized(true);
    setNotes('');
    setClientName('');
    setClientEmail('');
    setClientPhone('');
    success('Copied contract', 'Update the client details, then save as a new contract.');
  }, [copyFromId, copyFromRes, isEdit, profileLogo, success]);

  useEffect(() => {
    if (!isEdit || !editRes?.data) return;
    const src = editRes.data;
    if (hydratedEditRef.current === src.id) return;
    if (['signed', 'cancelled'].includes(String(src.status || '').toLowerCase())) {
      showError('This contract cannot be edited.');
      navigate(`/vendor/contracts/${editId}`, { replace: true });
      return;
    }
    hydratedEditRef.current = src.id;
    setTitle(src.title || '');
    setBodyHtml(src.body_html || '');
    setTemplateId(src.template_id || '');
    setClientName(src.client_name || '');
    setClientEmail(src.client_email || '');
    setClientPhone(src.client_phone || '');
    setNotes(src.notes || '');
    setLogoUrl(src.logo_url || profileLogo || null);
    setLogoInitialized(true);
  }, [isEdit, editRes, editId, navigate, showError, profileLogo]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => String(t.id) === String(templateId)) || null,
    [templates, templateId]
  );

  const placeholderVars = useMemo(
    () => ({
      vendor_name: vp.business_name || 'Your business',
      vendor_address: vendorAddress || '—',
      client_name: clientName.trim() || 'Client',
      client_email: clientEmail.trim() || '—',
      service_description: serviceDescription.trim() || 'Services as agreed',
      event_date: eventDate
        ? new Date(eventDate + 'T12:00:00').toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        : 'To be confirmed',
      amount: amount ? `$${Number(amount).toFixed(2)}` : 'As invoiced',
      contract_date: new Date().toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
    }),
    [vp.business_name, vendorAddress, clientName, clientEmail, serviceDescription, eventDate, amount]
  );

  const applyTemplate = (tpl) => {
    if (!tpl) return;
    setTemplateId(tpl.id);
    setTitle(tpl.name);
    setBodyHtml(applyContractPlaceholders(tpl.body_html, placeholderVars));
  };

  const refreshPlaceholders = () => {
    const source = selectedTemplate?.body_html || bodyHtml;
    if (!source) return;
    setBodyHtml(applyContractPlaceholders(source, placeholderVars));
  };

  const createMutation = useMutation({
    mutationFn: (payload) => vendorContractApi.create(payload),
    onSuccess: (res) => {
      success('Contract created', 'You can send a signing link or email next.');
      queryClient.invalidateQueries({ queryKey: ['vendor-contracts'] });
      navigate(`/vendor/contracts/${res?.data?.id}`);
    },
    onError: (err) => {
      showError('Create failed', err?.response?.data?.message || 'Could not create contract.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload) => vendorContractApi.update(editId, payload),
    onSuccess: (res) => {
      success('Contract updated', 'Your changes have been saved.');
      queryClient.invalidateQueries({ queryKey: ['vendor-contracts'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-contract', editId] });
      navigate(`/vendor/contracts/${editId}`, { replace: true });
    },
    onError: (err) => {
      showError('Update failed', err?.response?.data?.message || 'Could not update contract.');
    },
  });

  const handleGenerateAi = async () => {
    if (!canUseAi || isGenerating) return;
    setAiError('');
    setIsGenerating(true);
    try {
      const prompt = [
        'Draft a professional service contract in clean HTML (use h2/h3/p/ul/li only).',
        `Provider business: ${placeholderVars.vendor_name}.`,
        `Client: ${placeholderVars.client_name} (${placeholderVars.client_email}).`,
        `Services: ${placeholderVars.service_description}.`,
        `Event/service date: ${placeholderVars.event_date}.`,
        `Amount: ${placeholderVars.amount}.`,
        aiPrompt.trim() ? `Additional instructions: ${aiPrompt.trim()}` : '',
        'Include sections for services, payment, cancellation, liability, and electronic signature acceptance.',
        'Do not wrap in markdown fences. Output HTML only.',
      ]
        .filter(Boolean)
        .join(' ');

      const response = await supportChatApi.sendMessage({ message: prompt, history: [] });
      const generated = normalizeChatReply(response?.data?.reply);
      if (!generated) {
        setAiError('Could not generate contract. Try again.');
        return;
      }
      let html = generated.trim();
      if (!html.includes('<')) {
        html = html
          .split(/\n\n+/)
          .map((p) => `<p>${p.replace(/\n/g, '<br/>')}</p>`)
          .join('');
      }
      setBodyHtml(html);
      if (!title.trim()) setTitle('Service Agreement');
    } catch (err) {
      setAiError(err?.response?.data?.message || 'Could not generate contract. Try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const bodyIsEmpty = !bodyHtml || bodyHtml.replace(/<[^>]*>/g, '').trim() === '';
  const saving = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!clientName.trim()) {
      showError('Missing client', 'Enter the client name.');
      return;
    }
    if (bodyIsEmpty) {
      showError('Missing content', 'Add contract content or pick a template.');
      return;
    }
    const payload = {
      title: title.trim() || 'Service Agreement',
      body_html: bodyHtml,
      client_name: clientName.trim(),
      client_email: clientEmail.trim() || null,
      client_phone: clientPhone.trim() || null,
      template_id: templateId || null,
      notes: notes.trim() || null,
      logo_url: logoUrl || null,
      status: isEdit ? undefined : 'draft',
    };
    if (isEdit) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate({ ...payload, status: 'draft' });
    }
  };

  if ((copyFromId && copyLoading) || (isEdit && editLoading)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (isEdit && editError) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-3">
        <p className="text-gray-700">Could not load contract.</p>
        <Link to="/vendor/contracts" className="text-primary-600 hover:underline text-sm">
          Back to contracts
        </Link>
      </div>
    );
  }

  const pageTitle = isEdit
    ? 'Edit contract'
    : copyFromId
      ? 'Copy contract for another client'
      : 'New contract';

  const pageSubtitle = isEdit
    ? 'Update client details, logo, or contract terms, then save.'
    : copyFromId
      ? 'Content was copied from an existing contract. Enter the new client and save.'
      : 'Pick a template, add your logo, fill client details, optionally draft with AI, then save and send for signature.';

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="max-w-5xl mx-auto space-y-4">
        <div>
          <Link
            to={isEdit ? `/vendor/contracts/${editId}` : '/vendor/contracts'}
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-3"
          >
            <ArrowLeft className="w-4 h-4" />
            {isEdit ? 'Back to contract' : 'Back to contracts'}
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{pageTitle}</h1>
          <p className="text-sm text-gray-600 mt-1">{pageSubtitle}</p>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
              <h2 className="text-sm font-semibold text-gray-900">Contract logo</h2>
              <BrandImagePicker
                label="Logo on this contract"
                value={logoUrl}
                onChange={setLogoUrl}
                hint="Shown on the signing page and PDF. Defaults to your business logo — change anytime."
                aspectClass="aspect-[2/1] max-h-36"
                uploadImageType="general"
              />
              {!logoUrl && profileLogo && (
                <button
                  type="button"
                  onClick={() => setLogoUrl(profileLogo)}
                  className="text-xs font-medium text-primary-600 hover:underline"
                >
                  Use business profile logo
                </button>
              )}
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
              <h2 className="text-sm font-semibold text-gray-900">Templates</h2>
              {templatesLoading ? (
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {templates.map((tpl) => (
                    <button
                      key={tpl.id}
                      type="button"
                      onClick={() => applyTemplate(tpl)}
                      className={`w-full text-left rounded-lg border px-3 py-2.5 transition-colors ${
                        String(templateId) === String(tpl.id)
                          ? 'border-gray-900 bg-gray-50'
                          : 'border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      <div className="text-sm font-medium text-gray-900">{tpl.name}</div>
                      {tpl.description && (
                        <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{tpl.description}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-gray-900">AI draft</h2>
                {!canUseAi && (
                  <Link to="/vendor/plan" className="text-xs text-primary-600 hover:underline">
                    Upgrade
                  </Link>
                )}
              </div>
              <textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                rows={3}
                disabled={!canUseAi}
                placeholder="e.g. Include a 50% deposit clause and weekend overtime rates"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-400"
              />
              {canUseAi ? (
                <button
                  type="button"
                  onClick={handleGenerateAi}
                  disabled={isGenerating}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-60"
                >
                  {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
                  {isGenerating ? 'Generating…' : 'Generate with AI'}
                </button>
              ) : (
                <span
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600/40 text-white text-sm font-medium cursor-not-allowed"
                  title="Upgrade to use AI"
                >
                  <Bot className="w-4 h-4" />
                  Generate with AI
                </span>
              )}
              {aiError && <p className="text-xs text-red-600">{aiError}</p>}
              {!canUseAi && (
                <p className="text-xs text-gray-500">AI drafting is available on paid plans.</p>
              )}
            </div>
          </div>

          <div className="lg:col-span-3 space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
              <Input
                label="Contract title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Service Agreement"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="Client name *"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  required
                />
                <Input
                  label="Client email"
                  type="email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  placeholder="For sending the signing link"
                />
                <Input
                  label="Client phone"
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                />
                <Input
                  label="Event / service date"
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                />
                <Input
                  label="Amount (optional)"
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service description</label>
                <textarea
                  value={serviceDescription}
                  onChange={(e) => setServiceDescription(e.target.value)}
                  rows={2}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Photo booth package for wedding reception"
                />
              </div>
              <button
                type="button"
                onClick={refreshPlaceholders}
                className="text-xs font-medium text-gray-600 hover:text-gray-900 underline"
              >
                Re-apply template placeholders with current details
              </button>
              <div>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <label className="block text-sm font-medium text-gray-700">Contract body</label>
                  <button
                    type="button"
                    onClick={() => setPreviewOpen(true)}
                    disabled={bodyIsEmpty}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700 hover:text-gray-900 disabled:opacity-40"
                  >
                    <Eye className="w-3.5 h-3.5" />
                    Preview
                  </button>
                </div>
                <ContractRichTextEditor
                  value={bodyHtml}
                  onChange={setBodyHtml}
                  placeholder="Select a template, generate with AI, or write your terms…"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Internal notes (optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button type="submit" loading={saving}>
                {isEdit ? 'Save changes' : 'Save contract'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setPreviewOpen(true)}
                disabled={bodyIsEmpty}
                icon={Eye}
              >
                Preview
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(isEdit ? `/vendor/contracts/${editId}` : '/vendor/contracts')}
              >
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </div>

      {previewOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[10050] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50"
            role="dialog"
            aria-modal="true"
            onClick={(e) => {
              if (e.target === e.currentTarget) setPreviewOpen(false);
            }}
          >
            <div className="bg-white w-full sm:max-w-3xl sm:rounded-xl shadow-xl max-h-[92vh] flex flex-col border border-gray-200 min-w-0 overflow-hidden">
              <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-gray-200 shrink-0">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900">Contract preview</h2>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{title || 'Untitled contract'}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setPreviewOpen(false)}
                  className="p-2 rounded-md text-gray-500 hover:bg-gray-100 shrink-0"
                  aria-label="Close preview"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="overflow-y-auto overflow-x-hidden p-4 sm:p-8 flex-1 min-w-0">
                {logoUrl && (
                  <img
                    src={logoUrl}
                    alt=""
                    className="h-14 w-auto max-w-[180px] object-contain mb-4 rounded-md border border-gray-100"
                  />
                )}
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                  {vp.business_name || 'Your business'}
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{title || 'Untitled contract'}</h3>
                <ContractBodyHtml
                  html={bodyHtml || '<p class="text-gray-400">Nothing to preview yet.</p>'}
                  className="text-gray-800"
                />
              </div>
              <div className="px-4 sm:px-6 py-3 border-t border-gray-200 flex justify-end shrink-0">
                <Button type="button" variant="outline" onClick={() => setPreviewOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default VendorContractCreate;
